import { createSelector } from '@reduxjs/toolkit';
import type { BaseTaskTodo } from 'models';
import type { RootState } from 'store';

import searchBy from 'shared/utils/searchBy';
import formatDate from 'shared/utils/formatDate';
import isWithinInterval from 'shared/utils/isWithinInterval';
import processTableFilterOptions from 'shared/utils/processTableFilterOptions';

const getUniqueStatusesOptions = (tasks: BaseTaskTodo[]) => {
  const statusesMap = new Map<number, NonNullable<BaseTaskTodo['Status']>>();

  for (const task of tasks) {
    if (!task.Status) continue;

    if (!statusesMap.has(task.Status.Value)) {
      statusesMap.set(task.Status.Value, task.Status);
    }
  }

  return Array.from(statusesMap.values());
};

export const selectAvailableStatuses = (state: RootState) =>
  state.todo.options.Status;
export const selectCanCreate = (state: RootState) =>
  state.todo.options.CanCreate;
export const selectTasks = (state: RootState) => state.todo.todoList;
export const selectStartDate = (state: RootState) => state.todo.startDate;
export const selectEndDate = (state: RootState) => state.todo.endDate;
export const selectDateFilter = (state: RootState) => state.todo.dateFilter;
export const selectSearchTerm = (state: RootState) => state.todo.searchTerm;

export const getFilteredTask = createSelector(
  selectTasks,
  selectStartDate,
  selectEndDate,
  (tasks, startDate, endDate) =>
    tasks.filter((task) => {
      if (!task.DueDate) return false;

      return isWithinInterval(new Date(task.DueDate), {
        start: startDate,
        end: endDate,
      });
    })
);

export const getTaskFilteredBySearchTerm = createSelector(
  getFilteredTask,
  selectSearchTerm,
  (tasks, searchTerm) =>
    searchBy(
      tasks,
      [
        (task) => task.Subject ?? '',
        (task) => task.Executor?.Name ?? '',
        (task) => task.Priority?.Label ?? '',
        (task) => (task.DueDate ? formatDate(new Date(task.DueDate)) : ''),
        (task) => task.Status?.Label ?? '',
      ],
      searchTerm
    )
);

export const selectStatusesOptions = createSelector(selectTasks, (tasks) =>
  processTableFilterOptions(getUniqueStatusesOptions(tasks))
);
